import { useState } from "react";
import { useServiceContext } from "../../contexts/service";
import { Spinner } from "../common/spinner";
import { useOnboardingProcess } from "../../lib/common/onboarding_steps";
import { useAuthContext } from "../../contexts/auth-context";

type EmailVerificationStatus = "idle" | "sending" | "sent" | "checking" | "verified";

export const useEmailVerification = () => {
    const { security } = useServiceContext();
    const [status, setStatus] = useState<EmailVerificationStatus>("idle");
    const [message, setMessage] = useState("Your email is not verified");
    const { refreshFBTokenId } = useAuthContext();
    const { refreshOnboardingProgress } = useOnboardingProcess();

    const sendVerificationEmailClickHandler = async () => {
        if (status === "sending") return;

        setStatus("sending");
        setMessage("Sending verification email...");

        try {
            await security.sendEmailVerification();

            setStatus("sent");
            setMessage("Verification link sent. If you don't receive it within a few minutes, please check your spam or junk folder.");
        } catch {
            setStatus("idle");
            setMessage("Failed to send verification email. Please try again.");
        }
    };

    const refreshOnboarding = async () => {
        await refreshFBTokenId();
        await refreshOnboardingProgress();
    };

    const reloadAfterVerificationClickHandler = async (): Promise<boolean> => {
        if (status !== "sent") return false;

        setStatus("checking");
        setMessage("Checking email verification...");

        const verified = await security.isUserEmailVerified();

        if (!verified) {
            setStatus("sent");
            setMessage("Your email is still not verified. Please try again later.");
            return false;
        }

        await refreshOnboarding();

        setStatus("verified");
        setMessage("Email verified successfully");

        return true;
    };

    return {
        status,
        message,
        sendVerificationEmailClickHandler,
        refreshOnboarding,
        reloadAfterVerificationClickHandler,
    };
};

type EmailVerifierProps = {
    onVerified: () => void;
};

export const EmailVerifier = ({ onVerified }: EmailVerifierProps) => {
    const { status, message, sendVerificationEmailClickHandler, reloadAfterVerificationClickHandler } = useEmailVerification();

    const handleConfirmedClick = async () => {
        const verified = await reloadAfterVerificationClickHandler();

        if (verified) {
            onVerified();
        }
    };

    return (
        <div className="w-100 text-center text-white">
            <i className="fa-solid fa-envelope-circle-check fa-2x text-info mb-2" aria-hidden="true" />
            <p className="small text-white-50 mb-3">We’ll send a secure link to your email address. Open it to verify your account.</p>
            <p
                className={`small mb-3 ${status === "idle" ? "text-warning fw-semibold" : status === "verified" ? "text-success fw-bold" : ""}`}
                role="status"
                aria-live="polite"
            >
                {message}
            </p>

            {status === "idle" && (
                <button type="button" className="btn btn-info fw-bold w-100" onClick={sendVerificationEmailClickHandler}>
                    <i className="fa-regular fa-paper-plane me-2" aria-hidden="true" />
                    Send verification email
                </button>
            )}

            {status === "sending" && (
                <button type="button" className="btn btn-info text-white w-100" disabled>
                    <Spinner /> Sending email…
                </button>
            )}

            {status === "checking" && (
                <button type="button" className="btn btn-info text-white w-100" disabled>
                    <Spinner /> Checking verification…
                </button>
            )}

            {status === "sent" && (
                <>
                    <button type="button" className="btn btn-success fw-bold w-100 mb-2" onClick={handleConfirmedClick}>
                        <i className="fa-solid fa-check me-2" aria-hidden="true" />
                        Click here if you have verified your email
                    </button>

                    <button type="button" className="btn btn-link btn-sm text-info" onClick={sendVerificationEmailClickHandler}>
                        Didn’t receive it? Resend email
                    </button>
                </>
            )}

            {status === "verified" && (
                <div className="text-success fw-semibold">
                    <i className="fa fa-check-circle me-2" aria-hidden="true" />
                    Email verified
                </div>
            )}
        </div>
    );
};
